import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Plus, Trash2, Package, Camera, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import NumberInput from "../NumberInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const emptyLine = () => ({
  product_id: null, product_code: "", description: "", unit: "Kg",
  quantity_kg: 0, pieces: 0, supplier_lot: "", expiry_date: "", origin: "", notes: "",
  born_in: "", raised_in: "", slaughtered_in: "", slaughter_code: "",
  cut_in: "", cutting_code: "", slaughter_date: "", ear_tag: "",
});

export const LabelScanButton = ({ onExtracted, idx = 0 }) => {
  const inputRef = useRef(null);
  const [scanning, setScanning] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/warehouse/inventory/scan-label`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      if (r.data.error) {
        toast.error(r.data.error);
      } else {
        onExtracted(r.data);
        toast.success("Etichetta letta — campi compilati automaticamente");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella scansione dell'etichetta");
    } finally {
      setScanning(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" disabled={scanning}
        onClick={() => inputRef.current?.click()}
        className="border-[#8A1C2A]/40 text-[#8A1C2A] hover:bg-[#8A1C2A]/5"
        data-testid={`scan-label-btn-${idx}`}>
        {scanning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Camera className="w-4 h-4 mr-1" />}
        {scanning ? "Lettura AI…" : "Scansiona etichetta"}
      </Button>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])} data-testid={`scan-label-input-${idx}`} />
    </>
  );
};

export const mergeScan = (line, d) => ({
  ...line,
  description: line.description || d.description || "",
  quantity_kg: d.weight_kg > 0 ? d.weight_kg : line.quantity_kg,
  pieces: d.pieces > 0 ? d.pieces : line.pieces,
  supplier_lot: d.supplier_lot || line.supplier_lot,
  expiry_date: d.expiry_date || line.expiry_date,
  origin: d.origin || line.origin,
  born_in: d.born_in || line.born_in,
  raised_in: d.raised_in || line.raised_in,
  slaughtered_in: d.slaughtered_in || line.slaughtered_in,
  slaughter_code: d.slaughter_code || line.slaughter_code,
  cut_in: d.cut_in || line.cut_in,
  cutting_code: d.cutting_code || line.cutting_code,
  slaughter_date: d.slaughter_date || line.slaughter_date,
  ear_tag: d.ear_tag || line.ear_tag,
});

export const ProductPicker = ({ onSelect, testId }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/products`, { params: { search: q.trim() } });
        setResults((r.data || []).slice(0, 15));
        setOpen(true);
      } catch { /* noop */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca articolo per codice o descrizione…" className="pl-8" data-testid={testId || "line-product-search"} />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-auto bg-white border border-stone-200 rounded-lg shadow-lg">
          {results.map((p) => (
            <button key={p.id} type="button" onClick={() => { onSelect(p); setQ(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-stone-50 border-b border-stone-100 last:border-0"
              data-testid={`line-product-opt-${p.code}`}>
              <span className="text-xs font-mono text-[#8A1C2A] font-semibold">{p.code}</span>
              <span className="block text-sm text-stone-800 truncate">{p.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const LineFields = ({ line, onChange, idx = 0 }) => {
  const set = (patch) => onChange({ ...line, ...patch });
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <ProductPicker testId={`line-product-search-${idx}`} onSelect={(p) => set({ product_id: p.id, product_code: p.code, description: p.description, unit: p.unit || "Kg" })} />
        </div>
        <LabelScanButton idx={idx} onExtracted={(d) => onChange(mergeScan(line, d))} />
      </div>
      {line.product_code && (
        <Badge variant="outline" className="font-mono text-[#8A1C2A] border-[#8A1C2A]/30" data-testid={`line-code-badge-${idx}`}>{line.product_code}</Badge>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <div className="sm:col-span-3">
          <Label className="text-xs text-stone-500">Descrizione *</Label>
          <Input value={line.description} onChange={(e) => set({ description: e.target.value })} placeholder="Descrizione articolo" data-testid={`line-description-${idx}`} />
        </div>
        <div>
          <Label className="text-xs text-stone-500">U.M.</Label>
          <Select value={line.unit} onValueChange={(v) => set({ unit: v })}>
            <SelectTrigger data-testid={`line-unit-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="Kg">Kg</SelectItem><SelectItem value="Pz">Pz</SelectItem><SelectItem value="CT">CT</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div>
          <Label className="text-xs text-stone-500">Peso tot. (Kg)</Label>
          <NumberInput value={line.quantity_kg} onChange={(e) => set({ quantity_kg: e.target.value })} data-testid={`line-kg-${idx}`} />
        </div>
        <div>
          <Label className="text-xs text-stone-500">Colli / Pezzi</Label>
          <NumberInput value={line.pieces} onChange={(e) => set({ pieces: e.target.value })} data-testid={`line-pieces-${idx}`} />
        </div>
        <div>
          <Label className="text-xs text-stone-500">Lotto fornitore</Label>
          <Input value={line.supplier_lot} onChange={(e) => set({ supplier_lot: e.target.value })} placeholder="es. 400983" data-testid={`line-supplier-lot-${idx}`} />
        </div>
        <div>
          <Label className="text-xs text-stone-500">Scadenza (TMC)</Label>
          <Input type="date" value={line.expiry_date || ""} onChange={(e) => set({ expiry_date: e.target.value })} data-testid={`line-expiry-${idx}`} />
        </div>
        <div>
          <Label className="text-xs text-stone-500">Origine</Label>
          <Input value={line.origin} onChange={(e) => set({ origin: e.target.value })} placeholder="es. BLACK ANGUS" data-testid={`line-origin-${idx}`} />
        </div>
      </div>
      <p className="text-[11px] font-semibold text-stone-400 uppercase tracking-wide pt-1">Tracciabilità etichetta (HACCP)</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div>
          <Label className="text-xs text-stone-500">Nato in</Label>
          <Input value={line.born_in} onChange={(e) => set({ born_in: e.target.value })} placeholder="ITALIA" data-testid={`line-born-${idx}`} />
        </div>
        <div>
          <Label className="text-xs text-stone-500">Allevato in</Label>
          <Input value={line.raised_in} onChange={(e) => set({ raised_in: e.target.value })} placeholder="ITALIA" data-testid={`line-raised-${idx}`} />
        </div>
        <div>
          <Label className="text-xs text-stone-500">Macellato in</Label>
          <Input value={line.slaughtered_in} onChange={(e) => set({ slaughtered_in: e.target.value })} placeholder="ITALIA" data-testid={`line-slaughtered-${idx}`} />
        </div>
        <div>
          <Label className="text-xs text-stone-500">N. macello</Label>
          <Input value={line.slaughter_code} onChange={(e) => set({ slaughter_code: e.target.value })} placeholder="IT2270M" data-testid={`line-slaughter-code-${idx}`} />
        </div>
        <div>
          <Label className="text-xs text-stone-500">Sezionato in</Label>
          <Input value={line.cut_in} onChange={(e) => set({ cut_in: e.target.value })} placeholder="ITALIA" data-testid={`line-cut-${idx}`} />
        </div>
        <div>
          <Label className="text-xs text-stone-500">N. sezionamento</Label>
          <Input value={line.cutting_code} onChange={(e) => set({ cutting_code: e.target.value })} placeholder="IT2270M" data-testid={`line-cutting-code-${idx}`} />
        </div>
        <div>
          <Label className="text-xs text-stone-500">Data macellazione</Label>
          <Input type="date" value={line.slaughter_date || ""} onChange={(e) => set({ slaughter_date: e.target.value })} data-testid={`line-slaughter-date-${idx}`} />
        </div>
        <div>
          <Label className="text-xs text-stone-500">Nr. auricolare</Label>
          <Input value={line.ear_tag} onChange={(e) => set({ ear_tag: e.target.value })} data-testid={`line-ear-tag-${idx}`} />
        </div>
      </div>
    </div>
  );
};

export const lineValid = (l) => (l.description || "").trim() && ((l.quantity_kg || 0) > 0 || (l.pieces || 0) > 0);

const NewShipmentDialog = ({ open, onClose, onCreated }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [ddtReference, setDdtReference] = useState("");
  const [ddtDate, setDdtDate] = useState("");
  const [shipDate, setShipDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSupplierId(""); setSupplierSearch(""); setDdtReference(""); setDdtDate("");
    setShipDate(new Date().toISOString().slice(0, 10)); setNotes(""); setLines([emptyLine()]);
    axios.get(`${API}/suppliers`).then((r) => setSuppliers(r.data || [])).catch(() => {});
  }, [open]);

  const filteredSuppliers = suppliers.filter((s) => (s.business_name || "").toLowerCase().includes(supplierSearch.toLowerCase()));
  const validLines = lines.filter(lineValid);
  const canSave = supplierId && validLines.length > 0 && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        supplier_id: supplierId,
        ddt_reference: ddtReference,
        ddt_date: ddtDate || null,
        date: shipDate,
        notes,
        lines: validLines.map((l) => ({ ...l, expiry_date: l.expiry_date || null, slaughter_date: l.slaughter_date || null })),
      };
      const r = await axios.post(`${API}/warehouse/inventory/shipments`, payload);
      toast.success(`Carico n. ${r.data.shipment.number} registrato (${r.data.lots.length} partite)`);
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio del carico");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5 text-[#8A1C2A]" />Nuovo Carico di Magazzino</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-stone-500">Fornitore *</Label>
              <Input value={supplierSearch} onChange={(e) => { setSupplierSearch(e.target.value); setSupplierId(""); }} placeholder="Cerca fornitore…" data-testid="shipment-supplier-search" />
              {supplierSearch && !supplierId && (
                <div className="mt-1 max-h-40 overflow-auto border border-stone-200 rounded-lg bg-white">
                  {filteredSuppliers.length === 0 && <p className="p-2 text-sm text-stone-500">Nessun fornitore trovato — crealo in Anagrafiche → Fornitori</p>}
                  {filteredSuppliers.slice(0, 20).map((s) => (
                    <button key={s.id} type="button" onClick={() => { setSupplierId(s.id); setSupplierSearch(s.business_name); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-stone-50 border-b border-stone-100 last:border-0" data-testid={`shipment-supplier-opt-${s.id}`}>
                      {s.business_name}
                    </button>
                  ))}
                </div>
              )}
              {supplierId && <Badge className="mt-1 bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid="shipment-supplier-selected">✓ {supplierSearch}</Badge>}
            </div>
            <div>
              <Label className="text-xs text-stone-500">Data carico</Label>
              <Input type="date" value={shipDate} onChange={(e) => setShipDate(e.target.value)} data-testid="shipment-date-input" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Rif. DDT fornitore</Label>
              <Input value={ddtReference} onChange={(e) => setDdtReference(e.target.value)} placeholder="es. A/9907" data-testid="shipment-ddt-ref-input" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Data DDT</Label>
              <Input type="date" value={ddtDate} onChange={(e) => setDdtDate(e.target.value)} data-testid="shipment-ddt-date-input" />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-stone-900">Righe / Partite ({validLines.length} valide)</h4>
              <Button variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, emptyLine()])} data-testid="shipment-add-line-btn">
                <Plus className="w-4 h-4 mr-1" />Aggiungi riga
              </Button>
            </div>
            {lines.map((line, idx) => (
              <div key={idx} className={`p-3 rounded-lg border ${lineValid(line) ? "border-stone-200 bg-white" : "border-dashed border-stone-300 bg-stone-50"}`} data-testid={`shipment-line-${idx}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-stone-500">Riga {idx + 1} → nuova partita automatica</span>
                  {lines.length > 1 && (
                    <button onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))} className="text-stone-400 hover:text-red-600" data-testid={`shipment-remove-line-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <LineFields line={line} idx={idx} onChange={(l) => setLines((ls) => ls.map((x, i) => (i === idx ? l : x)))} />
              </div>
            ))}
          </div>

          <div>
            <Label className="text-xs text-stone-500">Note carico</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note facoltative" data-testid="shipment-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="shipment-cancel-btn">Annulla</Button>
          <Button onClick={submit} disabled={!canSave} className="bg-[#8A1C2A] hover:bg-[#701622]" data-testid="shipment-save-btn">
            {saving ? "Salvataggio…" : `Registra carico (${validLines.length} partite)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewShipmentDialog;
